import { isValidElement, type Key, type ReactNode, useCallback, useMemo, useRef, useEffect } from 'react';
import { AntdApp, MessageArgsProps, NotificationArgsProps } from '@commutifi-fe/ui';
import { logger } from '@commutifi-fe/logger';
import { Optional } from 'utility-types';
import { IntlMessageKeys, useComponentIntl } from '../locales';
import { useCommutifiErrorMessages } from './useCommutifiErrorMessages';
type UseNotificationApi = Optional<NotificationArgsProps, 'message'>;
export enum ErrorSeverity {
  Error = 'error',
  Warning = 'warning',
}
export type HandleErrorOptions = {
  fallbackMessage?: string;
  severity?: ErrorSeverity;
  config?: Omit<UseNotificationApi, 'description'>;
};
const LOG_LEVEL: Record<ErrorSeverity, 'error' | 'warning'> = {
  [ErrorSeverity.Error]: 'error',
  [ErrorSeverity.Warning]: 'warning'
};
const typeSafe = (config: Optional<MessageArgsProps, 'content'> | ReactNode): config is Optional<MessageArgsProps, 'content'> & {
  contentId?: IntlMessageKeys<'errors'>;
} => Boolean(config && (Object.prototype.hasOwnProperty.call(config, 'content') || Object.prototype.hasOwnProperty.call(config, 'contentId')));
const DEFAULT_MESSAGE_DURATION = 6;
/**
 * Hook that centralize antd notification and message APIs. The goal is to make it more
 * intuitive and easier to use notification vs. messages (which are really similar; See antd doc).
 *
 * In most error cases we will use what is called a 'notification' that offer the option to be closed
 * and closes itself after a longer time than messages.
 * Vs. Successes where we want a quick 'message' that will never need any user interaction
 *
 * Also, the error notification is wrapped in our function that will define a message prop if
 * none is provider (Default to 'Error'):
 * @example [ICON] Error
 *                 Custom description of the error
 *
 *
 * @returns Antd notification API wrapped in our functions to set some default values
 *          and antd message api
 */
export const useNotifications = () => {
  const intl = useComponentIntl('errors');
  const {
    message: appMessage,
    notification
  } = AntdApp.useApp();
  const {
    translateCommutifiError,
    translateCommutifiSuccess,
    translate
  } = useCommutifiErrorMessages();

  // Track active message keys and their destroy functions to prevent duplicates
  const activeMessagesRef = useRef<Map<Key, () => void>>(new Map());
  const getNotificationConfig = useCallback(({
    title,
    description,
    ...config
  }: UseNotificationApi) => ({
    title,
    description,
    ...config
  }), []);

  // Uses the antd message API for successes with optional key-based deduplication
  const success = useCallback((config_0?: (Optional<MessageArgsProps, 'content'> & {
    contentId?: IntlMessageKeys<'errors'>;
    key?: Key;
  }) | ReactNode) => {
    let finalConfig: any;
    let key: Key | undefined;
    if (typeof config_0 === 'string') {
      finalConfig = config_0;
    } else if (typeof config_0 === 'object' && config_0 && !isValidElement(config_0)) {
      const {
        key: configKey,
        content,
        ...restConfig
      } = config_0 as Optional<MessageArgsProps, 'content'> & {
        key?: Key;
        contentId?: IntlMessageKeys<'errors'>;
      };
      key = configKey;
      finalConfig = typeSafe(config_0) ? {
        ...restConfig,
        content: content || intl.formatMessage({
          id: 'global.success'
        })
      } : config_0;
    } else {
      finalConfig = config_0;
    }
    if (key) {
      // If there's an existing message, destroy it first
      const existingDestroy = activeMessagesRef.current.get(key);
      if (existingDestroy) {
        existingDestroy();
        activeMessagesRef.current.delete(key);
        // Small delay for exit animation then show new message
        setTimeout(() => showNewMessage(), 150);
        return;
      }
      // No existing message, show immediately
      return showNewMessage();
      function showNewMessage() {
        const definedKey = key || 'default-key';
        // Use unique internal key to avoid Ant Design's duration caching issues
        const uniqueKey = `${String(definedKey)}-${Date.now()}`;
        const destroy = appMessage.success({
          ...finalConfig,
          key: uniqueKey
        }, DEFAULT_MESSAGE_DURATION, () => activeMessagesRef.current.delete(definedKey));

        // Track the destroy function for this key
        activeMessagesRef.current.set(definedKey, destroy);
        return destroy;
      }
    }
    return appMessage.success(finalConfig, DEFAULT_MESSAGE_DURATION);
  }, [intl, appMessage]);

  // Cleanup messages when component unmounts
  useEffect(() => {
    const activeMessages = activeMessagesRef.current;
    return () => {
      // Destroy all tracked messages
      activeMessages.forEach(destroy => destroy());
      activeMessages.clear();
    };
  }, []);

  /**
   * Used specifically to find the URL param names 'success' and use that as a key
   * with the success messages mapping to display a message on screen.
   *
   * Accepts a fallbackMessage (intl translated message) to use if the success param isn't matching
   * any errors in the Map
   */
  const handleUrlSuccessParam = useCallback((successText: string, options?: {
    fallbackMessage?: string;
  }) => {
    appMessage.success(translateCommutifiSuccess(successText, options?.fallbackMessage));
  }, [appMessage, translateCommutifiSuccess]);

  // Error wrapper. Message defaults to 'Error'
  const error = useCallback((config: UseNotificationApi) => notification.error(getNotificationConfig({
    ...config,
    title: !config.title ? intl.formatMessage({
      id: 'global.error'
    }) : config.title
  })), [getNotificationConfig, intl, notification]);
  const warningNotification = useCallback((config: UseNotificationApi) => notification.warning(getNotificationConfig({
    ...config,
    title: !config.title ? intl.formatMessage({
      id: 'global.warning'
    }) : config.title
  })), [getNotificationConfig, intl, notification]);
  const severityNotification = useMemo(() => ({
    error,
    warning: warningNotification
  }) satisfies Record<ErrorSeverity, (config: UseNotificationApi) => void>, [error, warningNotification]);

  /**
   * Tries to find the best error message match based on commutifi error code or HTTP code
   * and displays the notification on screen.
   *
   * Accepts a fallbackMessage if no good match is found.
   * When severity is 'warning', notification uses warning styling and logger records as warning.
   */
  const handleError = useCallback((err: any, options?: HandleErrorOptions) => {
    const severity = options?.severity ?? ErrorSeverity.Error;
    logger[LOG_LEVEL[severity]](String(err), {
      operation: 'handle_notification_error',
      error: err
    });
    severityNotification[severity]({
      description: translateCommutifiError(err, options?.fallbackMessage),
      ...options?.config
    });
  }, [severityNotification, translateCommutifiError]);
  const handleErrorCode = useCallback((code: string | number | undefined) => {
    error({
      description: translate(code)
    });
  }, [error, translate]);
  return {
    success,
    error,
    info: notification.info,
    warning: notification.warning,
    open: notification.open,
    close: (key?: Key | undefined) => notification.destroy(key),
    destroy: (key?: Key | undefined) => notification.destroy(key),
    messages: appMessage,
    handleError,
    handleErrorCode,
    handleUrlSuccessParam,
    translate
  };
};